import { create } from 'zustand';
import type { User, LoginCredentials, CrmRole } from '../types';
import { authService } from '../services/auth.service';
import { userService } from '../services/user.service';

// Roles that count as "manager OR above" for CRM purposes. Mirror of the
// User#crm_manager? helper on the Rails side. Server is the source of truth;
// this flag is for client-side UX gating only.
export const CRM_MANAGER_ROLES: ReadonlyArray<CrmRole> = ['manager', 'ceo', 'client_manager'];

export const isCrmManager = (user: User | null | undefined): boolean =>
  !!user?.crm_role && CRM_MANAGER_ROLES.includes(user.crm_role);

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  login: (credentials: LoginCredentials) => Promise<void>;
  signInWithMicrosoft: (code: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  updateUser: (user: User) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  initialize: () => Promise<void>;

  // Derived selectors
  isCrmManager: () => boolean;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,

  login: async (credentials: LoginCredentials) => {
    set({ isLoading: true, error: null });
    try {
      const { user, token } = await authService.login(credentials);
      set({
        user,
        token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      const errorMessage = err.response?.data?.error || 'Login failed. Please try again.';
      set({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: errorMessage,
      });
      throw error;
    }
  },

  signInWithMicrosoft: async (code: string) => {
    set({ error: null });
    try {
      const { user, token } = await authService.exchangeMicrosoftCode(code);
      set({ user, token, isAuthenticated: true, error: null });
    } catch (error: unknown) {
      set({ user: null, token: null, isAuthenticated: false });
      throw error;
    }
  },

  logout: async () => {
    set({ isLoading: true });
    try {
      await authService.logout();
    } finally {
      set({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    }
  },

  refreshUser: async () => {
    const token = authService.getToken();
    if (!token) return;

    set({ isLoading: true });
    const freshUser = await userService.getCurrentUser();
    set({
      user: freshUser,
      token,
      isAuthenticated: true,
      isLoading: false
    });
  },

  updateUser: (user: User) => {
    set({ user });
    localStorage.setItem('user', JSON.stringify(user));
  },

  setError: (error: string | null) => {
    set({ error });
  },

  clearError: () => {
    set({ error: null });
  },

  // Derived selector: true if the current user holds a manager-tier CRM role
  // (manager / ceo / client_manager). Mirrors the Rails User#crm_manager? helper.
  isCrmManager: () => isCrmManager(get().user),

  initialize: async () => {
    set({ isLoading: true });
    const token = authService.getToken();

    if (token) {
      // Always validate token with backend on page load
      try {
        await get().refreshUser();
      } catch {
        // If validation fails, clear the invalid token
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false
        });
      }
    } else {
      set({ isLoading: false });
    }
  },
}));
